/**
 * Link my Food — brand logo.
 *
 * The mark is the brand's core idea drawn literally: two interlocking chain
 * links (orange over navy) flanked by a fork and a spoon — "Link" + "Food".
 * Colours come from the brand sheet: orange #FF7300, navy #02142C.
 *
 * Props:
 *   variant  'full' (mark + wordmark) | 'mark' (mark only)
 *   height   rendered height in px (everything scales from this)
 *   onDark   invert the navy parts to white for dark backgrounds
 */
const NAVY = '#02142C';
const ORANGE = '#FF7300';

export const BrandMark = ({ size = 44, onDark = false, title = 'Link my Food', compact }) => {
  const ink = onDark ? '#FFFFFF' : NAVY;
  // Responsive logo: below ~34px the fork/spoon strokes fall under a pixel and
  // turn to mush, so small sizes render the interlocking links alone.
  const linksOnly = compact ?? size < 34;
  // Geometry measured from the brand artwork: large interlocking links as the
  // hero element, with a slim fork and spoon set out to either side.
  return (
    <svg
      viewBox={linksOnly ? '22 -1 84 136' : '0 0 140 136'}
      width={size * ((linksOnly ? 84 : 140) / 136)}
      height={size}
      role="img"
      aria-label={title}
      focusable="false"
    >
      <title>{title}</title>

      {/* Fork — three tines, shoulder, tapered handle */}
      {!linksOnly && (
        <g fill={ink}>
          <rect x="4.4" y="33" width="3.2" height="27" rx="1.6" />
          <rect x="9.4" y="33" width="3.2" height="27" rx="1.6" />
          <rect x="14.4" y="33" width="3.2" height="27" rx="1.6" />
          <path d="M4.4 57h13.2v4.4a6.6 6.6 0 0 1-13.2 0z" />
          <path d="M9.2 67h3.6l-.72 48a1 1 0 0 1-2.16 0z" />
        </g>
      )}

      {/* Interlocking links — the mark's hero, offset diagonally so the
          chain reads clearly: orange upper-left, navy lower-right. */}
      <rect
        x="28" y="4" width="48" height="88" rx="24"
        fill="none" stroke={ORANGE} strokeWidth="8.5"
      />
      <rect
        x="52" y="42" width="48" height="88" rx="24"
        fill="none" stroke={ink} strokeWidth="8.5"
      />
      {/* Orange re-crosses over navy at the lower-left join → true chain interlock */}
      <path
        d="M28 68A24 24 0 0 0 52 92"
        fill="none" stroke={ORANGE} strokeWidth="8.5" strokeLinecap="round"
      />

      {/* Spoon — bowl + tapered handle */}
      {!linksOnly && (
        <g fill={ink}>
          <ellipse cx="124" cy="46" rx="10.2" ry="14.6" />
          <path d="M122.2 60h3.6l-.72 55a1 1 0 0 1-2.16 0z" />
        </g>
      )}

      {/* Accent dots, as on the brand sheet */}
      {!linksOnly && <circle cx="92" cy="13" r="4.8" fill={ORANGE} />}
      {!linksOnly && <circle cx="36" cy="110" r="5.2" fill={ink} />}
    </svg>
  );
};

const BrandLogo = ({ variant = 'full', height = 44, onDark = false, showTagline = false }) => {
  const ink = onDark ? '#FFFFFF' : NAVY;

  if (variant === 'mark') {
    return <BrandMark size={height} onDark={onDark} />;
  }

  return (
    <span className="lmf-logo" style={{ display: 'inline-flex', alignItems: 'center', gap: height * 0.22 }}>
      <span data-brand-mark style={{ display: 'inline-flex' }}>
        <BrandMark size={height} onDark={onDark} />
      </span>
      <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1 }}>
        <span
          data-brand-name
          style={{
            fontFamily: "'Poppins', 'Inter', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: height * 0.62,
            letterSpacing: '-0.02em',
            color: ink,
            whiteSpace: 'nowrap'
          }}
        >
          Link<span style={{ color: ORANGE }}>my</span>Food
        </span>
        {showTagline && (
          <span
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: height * 0.17,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: onDark ? 'rgba(255,255,255,.72)' : 'rgba(2,20,44,.62)',
              marginTop: height * 0.1,
              whiteSpace: 'nowrap'
            }}
          >
            One link. Every food experience.
          </span>
        )}
      </span>
    </span>
  );
};

export default BrandLogo;
